'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA } from '@/app/components/blog/BlogAeo'
import RiskQuizInlineNudge from '@/app/components/blog/RiskQuizInlineNudge'
import KeyStats from '@/app/components/blog/KeyStats'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Does Simply Gum Have Microplastics? The Honest Chicle Verdict (2026)"
      author="MicroPlastics Research Desk"
      date="July 15, 2026"
      readTime="9 min read"
      heroImage="/blog/heroes/food.webp"
      heroImageAlt="Does Simply Gum have microplastics — the chicle tree-sap base audited against the 2025 UCLA study"
    >
      <BlogLastUpdated iso="2026-07-15" formatted="July 15, 2026" />

      <BlogTLDR>
        <strong>It has no synthetic plastic gum base — but &ldquo;plastic-free&rdquo;
        is not the same as &ldquo;sheds no microplastics.&rdquo;</strong>{' '}
        Simply Gum is one of the few brands with a genuinely clean label: its base is{' '}
        <strong>chicle</strong> (sapodilla tree sap), not the petroleum polymers hidden
        inside mainstream &ldquo;gum base.&rdquo; So if your question is <em>&ldquo;am I
        chewing plastic?&rdquo;</em>, Simply Gum is a real upgrade over Trident or Orbit.
        The honest complication: the 2025 UCLA pilot study found that{' '}
        <strong>natural chicle gums shed microplastic particles at levels similar to
        synthetic gums</strong> — likely because chicle is itself a natural polymer that
        fragments as you chew. So Simply Gum wins clearly on <strong>ingredients</strong>,
        but is not proven to eliminate particle shedding. It&apos;s the best gum choice,
        with realistic expectations.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>Simply Gum&apos;s base is <strong>chicle (sapodilla tree sap)</strong>, plus candelilla wax and citric acid — a short, fully disclosed, plant-based label.</>,
          <>It contains <strong>no synthetic gum base</strong>: no polyvinyl acetate, polyethylene, or petroleum rubbers. That is a real advantage over mainstream gum.</>,
          <>But the 2025 UCLA study found <strong>natural and synthetic gums shed similar microplastic counts</strong> (~100&ndash;600 particles per gram), so chicle is not proven to shed zero.</>,
          <>Chicle is a <strong>natural polymer</strong>; as it breaks down while chewing, it can fragment into particles the same instruments count as &ldquo;microplastics.&rdquo;</>,
          <>Verdict: <strong>best gum choice for a clean ingredient list</strong>, but treat &ldquo;plastic-free&rdquo; as &ldquo;no petroleum plastic,&rdquo; not &ldquo;no particles.&rdquo;</>,
          <>The one reliable reduction is still <strong>chewing less</strong>. Compare all brands in <Link href="/blog/chewing-gum-brands-ranked-microplastics" className="text-forest underline">gum brands ranked</Link>.</>,
        ]}
      />

      <RiskQuizInlineNudge source="blog_inline_simply_gum" />

      <KeyStats
        heading="Simply Gum & microplastics — the facts"
        stats={[
          { value: 'Chicle', label: 'Simply Gum base', context: 'sapodilla tree sap — a natural polymer, no petroleum plastic', source: 'Simply Gum ingredients', sourceUrl: 'https://simplygum.com/' },
          { value: 'No synthetic', label: 'gum base polymers', context: 'no polyvinyl acetate, polyethylene, or butyl rubber', source: 'Product labeling', sourceUrl: 'https://simplygum.com/' },
          { value: 'Similar', label: 'shedding to synthetic gum', context: 'UCLA 2025 found natural ≈ synthetic microplastic counts', source: 'Mohanty / UCLA, ACS Spring 2025', sourceUrl: 'https://newsroom.ucla.edu/releases/bursting-your-bubble-chewing-gum-releases-microplastics-into-your-saliva-ucla-research-shows' },
          { value: '100–600/g', label: 'particles across all gum types', context: 'the study measured both natural and synthetic brands', source: 'UCLA 2025 pilot study', sourceUrl: 'https://www.acs.org/pressroom/presspacs/2025/march/chewing-gum-can-shed-microplastics-into-saliva-pilot-study-finds.html' },
        ]}
      />

      <h2>Does Simply Gum contain microplastics?</h2>
      <p>
        This is the most nuanced answer of any gum. On <strong>ingredients</strong>,
        Simply Gum is clean: its base is <strong>chicle</strong>, the sap of the
        sapodilla tree, alongside candelilla wax, organic cane sugar or a natural
        sweetener, natural flavors, and citric acid. There is no synthetic gum base —
        none of the polyvinyl acetate, polyethylene, or petroleum rubbers that
        mainstream brands bury under the words &ldquo;gum base.&rdquo; If you have been
        chewing gum uneasy about literally chewing plastic, Simply Gum resolves that.
      </p>
      <p>
        But the 2025 UCLA pilot study complicates the marketing. Researchers tested five
        synthetic <em>and</em> five natural gums and found they released{' '}
        <strong>similar amounts of microplastic particles</strong> — roughly
        100&ndash;600 per gram. The likely reason is that <strong>chicle is itself a
        polymer</strong> (a natural one), and as any gum breaks down mechanically in your
        mouth it fragments into particles that the study&apos;s instruments detect and
        count. So &ldquo;plastic-free&rdquo; on the box is an honest claim about the
        petroleum content, not a guarantee of zero shedding.
      </p>

      <h2>So is it worth switching to?</h2>
      <p>
        Yes — with the right expectation. Simply Gum is the <strong>best gum choice</strong>{' '}
        if you want a short, fully disclosed, plant-based ingredient list and no petroleum
        polymers. That is a genuine improvement over Trident or Orbit, whose bases are
        undisclosed synthetic plastic. What it is <em>not</em> is a proven way to chew gum
        with zero microplastic release. If eliminating particle shedding entirely is the
        goal, the only reliable move is to <strong>chew less gum</strong>, or none.
      </p>

      <BlogComparisonTable
        caption="Simply Gum vs mainstream gum"
        columns={[
          { key: 'gum', label: 'Gum' },
          { key: 'base', label: 'Base' },
          { key: 'ingredients', label: 'Ingredient quality' },
          { key: 'shedding', label: 'Microplastic shedding' },
        ]}
        rows={[
          { gum: 'Simply Gum (and other chicle gums)', base: 'Chicle (tree sap)', ingredients: 'Clean, plant-based, disclosed', shedding: 'Similar to synthetic (UCLA 2025)' },
          { gum: 'Trident / Orbit / Extra', base: 'Synthetic (petroleum)', ingredients: 'Undisclosed "gum base"', shedding: 'Sheds microplastics' },
          { gum: 'No gum (mints, veg)', base: 'None', ingredients: 'n/a', shedding: 'None from gum' },
        ]}
      />
      <p>
        Read the{' '}
        <Link href="/blog/does-trident-have-microplastics" className="text-forest underline">Trident</Link>{' '}
        and{' '}
        <Link href="/blog/does-orbit-gum-have-microplastics" className="text-forest underline">Orbit</Link>{' '}
        verdicts, the full{' '}
        <Link href="/blog/chewing-gum-brands-ranked-microplastics" className="text-forest underline">gum brands ranked</Link>, and the{' '}
        <Link href="/blog/chewing-gum-brands-ranked-microplastics" className="text-forest underline">microplastics in chewing gum</Link>{' '}
        explainer.
      </p>

      <BlogAppCTA
        headline="Clean gum is a start — audit the rest of your day"
        sub="Simply Gum fixes the ingredient list; the app finds the plastic hiding in the packaged foods, drinks, and containers you use daily. Scan them with the MicroPlastics app."
      />

      <BlogFAQ
        items={[
          {
            question: 'Is Simply Gum actually plastic-free?',
            answer: 'Simply Gum contains no synthetic, petroleum-based gum base — its base is chicle, a natural tree sap. So it is free of the plastic gum base used in mainstream brands. However, "plastic-free" describes the ingredients, not a guarantee that no particles shed while chewing.',
          },
          {
            question: 'Does chicle gum still release microplastics?',
            answer: 'The 2025 UCLA study found natural chicle gums released microplastic particle counts similar to synthetic gums, likely because chicle is a natural polymer that fragments as you chew. So chicle gum has cleaner ingredients but is not proven to shed zero particles.',
          },
          {
            question: 'Is Simply Gum better than Trident or Orbit?',
            answer: 'For ingredients, clearly yes: Simply Gum uses a disclosed, plant-based chicle base with no petroleum polymers, while Trident and Orbit use undisclosed synthetic gum base. On microplastic shedding specifically, the UCLA study found similar amounts across gum types.',
          },
          {
            question: 'What gum has the fewest microplastics?',
            answer: 'No gum has been shown to shed zero microplastics. Chicle-based gums like Simply Gum are the best choice for avoiding synthetic plastic ingredients, but the reliable way to reduce gum-derived microplastics is to chew less gum, for less time.',
          },
        ]}
      />

      <BlogSources
        items={[
          { authors: 'Mohanty S, et al. (UCLA)', year: 2025, title: 'Chewing Gums: Unintended sources of ingested microplastics in humans (ACS Spring 2025)', publisher: 'American Chemical Society', url: 'https://www.acs.org/pressroom/presspacs/2025/march/chewing-gum-can-shed-microplastics-into-saliva-pilot-study-finds.html' },
          { authors: 'UCLA Newsroom', year: 2025, title: 'Chewing gum releases microplastics into your saliva, UCLA research shows', publisher: 'UCLA', url: 'https://newsroom.ucla.edu/releases/bursting-your-bubble-chewing-gum-releases-microplastics-into-your-saliva-ucla-research-shows' },
          { authors: 'World Health Organization', year: 2022, title: 'Dietary and inhalation exposure to nano- and microplastic particles', publisher: 'WHO', url: 'https://www.who.int/publications/i/item/9789240054608' },
        ]}
      />
    </BlogPostLayout>
  )
}
